import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowsClockwise, CheckCircle, Warning, X, CloudArrowDown } from "@phosphor-icons/react";
import api from "../lib/api";
import { useDiagnostics, confirmProductionWrite } from "../lib/diagnostics";
import { toast } from "sonner";
import LiveListingIngestCard from "./LiveListingIngestCard";
import OddsEnrichmentCard from "./OddsEnrichmentCard";
import LiveRepairCard from "./LiveRepairCard";
import DcgPipelineV2Card from "./DcgPipelineV2Card";
import V2ResetMaintenanceCard from "./V2ResetMaintenanceCard";
import TrustedAutomationCard from "./TrustedAutomationCard";
import OperatorAutomationSettingsCard from "./OperatorAutomationSettingsCard";
import OperatorAutomationRunCard from "./OperatorAutomationRunCard";
import OperatorExecutionLeaseCard from "./OperatorExecutionLeaseCard";
import {
  buildAdminOperatorOptions,
  mergeAutomationSettingsIntoOperator,
} from "../lib/adminOperatorModes";

const STATUS_STYLES = {
  ok: "bg-emerald-500/20 text-emerald-300",
  running: "bg-amber-500/20 text-amber-300",
  blocked: "bg-red-500/20 text-red-300",
  error: "bg-red-500/20 text-red-300",
};

export default function IngestRunsPanel() {
  const [operators, setOperators] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedOperatorOptionKey, setSelectedOperatorOptionKey] = useState("dcg");
  const [automationSettings, setAutomationSettings] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState({ key: null, settings: null, error: "", loading: false });
  const [coreVersion, setCoreVersion] = useState(0);
  const [selectedExecutionLeaseStatus, setSelectedExecutionLeaseStatus] = useState(null);
  const [executionLeaseRefreshToken, setExecutionLeaseRefreshToken] = useState(0);
  const [busyOp, setBusyOp] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [coreState, setCoreState] = useState("loading");
  const [runsState, setRunsState] = useState("loading");
  const [coreError, setCoreError] = useState("");
  const [runsError, setRunsError] = useState("");
  const [coreReadyMs, setCoreReadyMs] = useState(null);
  const [runsElapsedMs, setRunsElapsedMs] = useState(null);
  const coreRequestId = useRef(0);
  const runsRequestId = useRef(0);
  const selectedAuditFlight = useRef(null);
  const selectedRunRefresh = useRef({ key: null, phase: "", revision: 0 });
  const hasCoreData = useRef(false);
  const {
    diag,
    diagnosticsState,
    diagnosticsError,
    diagnosticsElapsedMs,
    refreshDiagnostics,
  } = useDiagnostics();

  const refreshCore = useCallback(async ({ signal } = {}) => {
    const requestId = ++coreRequestId.current;
    const startedAt = Date.now();
    if (!hasCoreData.current) setCoreState("loading");
    setCoreError("");
    setCoreReadyMs(null);
    try {
      const [ops, automation] = await Promise.all([
        api.get("/admin/operators", { signal }),
        api.get("/admin/operator-automation-settings", { signal }),
      ]);
      if (signal?.aborted || requestId !== coreRequestId.current) return;
      setOperators(ops.data);
      setAutomationSettings(automation.data?.items || []);
      setCoreVersion((version) => version + 1);
      hasCoreData.current = true;
      setCoreReadyMs(Date.now() - startedAt);
      setCoreState("ready");
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") return;
      if (requestId !== coreRequestId.current) return;
      setCoreError(
        error?.response?.data?.detail || error?.message || "Operator controls unavailable",
      );
      setCoreReadyMs(Date.now() - startedAt);
      setCoreState(hasCoreData.current ? "stale" : "error");
    }
  }, []);

  const refreshRuns = useCallback(async ({ signal } = {}) => {
    const requestId = ++runsRequestId.current;
    const startedAt = Date.now();
    setRunsState("loading");
    setRunsError("");
    setRunsElapsedMs(null);
    try {
      const response = await api.get("/admin/ingest-runs", { signal });
      if (signal?.aborted || requestId !== runsRequestId.current) return;
      setRuns(response.data);
      setRunsElapsedMs(Date.now() - startedAt);
      setRunsState("ready");
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") return;
      if (requestId !== runsRequestId.current) return;
      setRunsError(
        error?.response?.data?.detail || error?.message || "Ingest history unavailable",
      );
      setRunsElapsedMs(Date.now() - startedAt);
      setRunsState("error");
    }
  }, []);

  const refresh = useCallback(() => {
    void refreshCore();
    void refreshRuns();
  }, [refreshCore, refreshRuns]);

  useEffect(() => {
    const controller = new AbortController();
    refreshCore({ signal: controller.signal });
    return () => controller.abort();
  }, [refreshCore]);

  useEffect(() => {
    const controller = new AbortController();
    refreshRuns({ signal: controller.signal });
    return () => controller.abort();
  }, [refreshRuns]);

  const adminOperatorOptions = useMemo(
    () => buildAdminOperatorOptions(operators),
    [operators],
  );
  const automationSettingsByKey = useMemo(() => {
    const out = {};
    for (const item of automationSettings || []) {
      if (item?.operator_key) out[item.operator_key] = item;
    }
    return out;
  }, [automationSettings]);
  const selectedOperatorBase =
    adminOperatorOptions.find((op) => op.optionKey === selectedOperatorOptionKey)
    || adminOperatorOptions[0]
    || null;
  useEffect(() => {
    const key = selectedOperatorBase?.key;
    if (selectedAuditFlight.current && selectedAuditFlight.current.key !== key) {
      selectedAuditFlight.current.controller.abort();
      selectedAuditFlight.current = null;
    }
    if (!key || selectedOperatorBase?.supports?.operatorAutomation !== true || coreState === "loading") {
      return undefined;
    }
    if (selectedAuditFlight.current?.key === key) return undefined;
    const controller = new AbortController();
    // Capture at launch so an older in-flight audit cannot certify a newer run.
    const runRefreshRevision = selectedRunRefresh.current.key === key
      ? selectedRunRefresh.current.revision
      : 0;
    selectedAuditFlight.current = { key, controller };
    setSelectedAudit((current) => ({
      key,
      settings: current.key === key ? current.settings : null,
      error: "",
      loading: true,
    }));
    api.get(`/admin/operator-automation-settings/${key}`, { signal: controller.signal })
      .then(({ data, status }) => {
        if (!controller.signal.aborted) {
          setSelectedAudit({ key, settings: data, error: "", loading: false, runRefreshRevision, httpStatus: status });
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted && error?.code !== "ERR_CANCELED") {
          setSelectedAudit({
            key,
            settings: null,
            error: error?.response?.data?.detail || error?.message || "Selected operator audit unavailable",
            loading: false,
          });
        }
      })
      .finally(() => {
        if (selectedAuditFlight.current?.controller === controller) {
          selectedAuditFlight.current = null;
        }
      });
    return undefined;
  }, [selectedOperatorBase?.key, selectedOperatorBase?.supports?.operatorAutomation, coreState, coreVersion]);
  useEffect(() => () => selectedAuditFlight.current?.controller.abort(), []);
  const selectedAutomationSettings = selectedOperatorBase?.supports?.operatorAutomation
    ? selectedAudit.key === selectedOperatorBase.key ? selectedAudit.settings : null
    : selectedOperatorBase ? automationSettingsByKey[selectedOperatorBase.key] || null : null;
  const selectedActionSettings = (
    selectedOperatorBase?.supports?.operatorAutomation && selectedAudit.loading
      ? null
      : selectedAutomationSettings
  );
  const selectedOperator = selectedOperatorBase
    ? mergeAutomationSettingsIntoOperator(
        selectedOperatorBase,
        selectedAutomationSettings,
      )
    : null;
  const selectedBackendOperator = selectedOperator?.operator || null;
  const selectedSettingsRequired = (
    selectedOperator?.supports?.operatorAutomation === true
  );
  const selectedSettingsLoaded = (
    !selectedSettingsRequired || Boolean(selectedAutomationSettings)
  );
  const operatorCoreReady = (
    (coreState === "ready" || coreState === "stale")
    && Boolean(selectedOperator)
    && selectedSettingsLoaded
  );
  // Keep run evidence mounted while a same-operator audit disables stale actions.
  const selectedControlsReady = (
    operatorCoreReady
    && (!selectedSettingsRequired || !selectedAudit.loading)
  );
  const operatorInventoryReady = (
    (coreState === "ready" || coreState === "stale")
    && adminOperatorOptions.length > 0
  );
  const operatorCoreFailed = (
    coreState === "error"
    || (
      (coreState === "ready" || coreState === "stale")
      && Boolean(selectedOperator)
      && !selectedSettingsLoaded
      && selectedAudit.key === selectedOperatorBase?.key
      && Boolean(selectedAudit.error)
    )
  );
  const secondaryState = (
    runsState === "error" || diagnosticsState === "error"
      ? "error"
      : runsState === "ready" && diagnosticsState === "ready"
      ? "ready"
      : "loading"
  );
  const outstandingRequests = [
    coreState === "loading" ? "operator_inventory_and_settings" : null,
    selectedSettingsRequired && (!selectedSettingsLoaded || selectedAudit.loading) ? "selected_operator_audit" : null,
    runsState === "loading" ? "ingest_history" : null,
    diagnosticsState === "loading" ? "diagnostics" : null,
  ].filter(Boolean);
  const readinessError = (
    coreError
    || runsError
    || diagnosticsError
    || selectedAudit.error
    || (!selectedSettingsLoaded && !selectedAudit.loading ? "Selected operator settings unavailable" : "")
  );
  const handleAutomationSettingsUpdated = (updated) => {
    if (selectedAuditFlight.current?.key === updated.operator_key) {
      selectedAuditFlight.current.controller.abort();
      selectedAuditFlight.current = null;
    }
    setSelectedAudit({ key: updated.operator_key, settings: updated, error: "", loading: false });
    setAutomationSettings((items) => {
      const found = (items || []).some(
        (item) => item.operator_key === updated.operator_key,
      );
      if (!found) return [...(items || []), updated];
      return items.map((item) => (
        item.operator_key === updated.operator_key ? updated : item
      ));
    });
  };

  useEffect(() => {
    if (
      adminOperatorOptions.length > 0
      && !adminOperatorOptions.some((op) => op.optionKey === selectedOperatorOptionKey)
    ) {
      setSelectedOperatorOptionKey(adminOperatorOptions[0].optionKey);
    }
  }, [adminOperatorOptions, selectedOperatorOptionKey]);

  useEffect(() => {
    setSelectedExecutionLeaseStatus(null);
  }, [selectedOperator?.key]);

  const [forceBraveRefresh, setForceBraveRefresh] = useState(false);

  const triggerBraveDiscovery = async (op) => {
    if (!op.brave_search_enabled) {
      toast.error("Brave search is not enabled for this operator.");
      return;
    }
    if (!confirmProductionWrite(diag, `Run Brave Search discovery for ${op.name} (writes candidates + pending_review records).`)) return;
    setBusyOp(op.id);
    try {
      const { data } = await api.post(
        `/admin/operators/${op.id}/brave-discovery?force_refresh=${forceBraveRefresh}`,
      );
      const reasonMap = {
        provider_error: "Brave provider error",
        provider_limit_reached: "Brave Search limit reached — check billing / credits",
        templates_missing: "Templates / canonical domain missing",
        no_raw_results: "Brave returned 0 raw results",
        all_results_rejected: "All results rejected (classifier / domain mismatch / duplicates)",
        all_duplicates: "All results were duplicates",
        partial: "Partial — some queries errored",
        ok: "OK",
      };
      const label = reasonMap[data.status] || data.status;
      const detail = [
        `raw=${data.results_total ?? 0}`,
        `new=${data.candidates_new ?? 0}`,
        `children=${data.child_candidates_spawned ?? 0}`,
        `dupes=${data.duplicates_skipped ?? 0}`,
        `rejected=${(data.candidates_rejected_by_classifier ?? 0) + (data.candidates_rejected_by_domain ?? 0)}`,
        `cache=${data.cache_hits ?? 0}`,
      ].join(" · ");
      if (data.status === "ok" || data.status === "partial") {
        toast.success(`Brave ${label}: ${detail}` + (data.error ? `\n${data.error}` : ""));
      } else {
        toast.error(
          `Brave ${label}` + (data.error ? `: ${data.error}` : "") + `\n(${detail})`,
        );
      }
      refresh();
      refreshDiagnostics();
    } catch (e) {
      toast.error("Brave discovery failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusyOp(null);
    }
  };

  const triggerIngest = async (op) => {
    if (!confirmProductionWrite(diag, `Run auto-ingest for ${op.name}.`)) return;
    setBusyOp(op.id);
    try {
      const { data } = await api.post(
        `/admin/operators/${op.id}/ingest?sync=true`,
        null,
      );
      toast.success(
        `Ingest ${data.status}: ${data.ingested_count || 0} created, ${data.updated_count || 0} updated, ${data.rejected_count || 0} rejected.`,
      );
      setSelected(data);
      refresh();
      refreshDiagnostics();
    } catch (e) {
      toast.error("Ingest failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusyOp(null);
    }
  };

  const triggerPrefillAll = async (op) => {
    if (!confirmProductionWrite(diag, `Prefill all pending candidates for ${op.name} (creates/updates source_pending_review records).`)) return;
    setBusyOp(op.id);
    try {
      const { data } = await api.post(
        `/admin/operators/${op.id}/prefill-all-candidates`,
        null,
        { params: { limit: 25 } },
      );
      toast.success(
        `Prefilled ${data.processed} candidates: ${data.successes} ok, ${data.errors} errors.`,
      );
      refresh();
      refreshDiagnostics();
    } catch (e) {
      toast.error("Prefill failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusyOp(null);
    }
  };

  const triggerOperatorEvidence = async (op) => {
    if (!confirmProductionWrite(diag, `Refresh operator metadata for ${op.name} from open evidence (Brave + OG).`)) return;
    setBusyOp(op.id);
    try {
      const { data } = await api.post(`/admin/operators/${op.id}/prefill-evidence`);
      const ev = data.evidence || {};
      const proposed = Object.keys(ev).length;
      toast.success(
        `Captured ${proposed} field suggestion${proposed === 1 ? "" : "s"} (${data.discovery_method})`,
      );
      refresh();
      refreshDiagnostics();
    } catch (e) {
      toast.error("Operator prefill failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusyOp(null);
    }
  };

  const syncOperatorDirectory = async () => {
    if (!confirmProductionWrite(diag, "Sync the built-in operator directory into the database.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-operator-directory");
      toast.success(
        `Operator directory synced: ${data.inserted_count} inserted, ${data.updated_count} updated, ${data.skipped_count} unchanged.`,
      );
      refresh();
      refreshDiagnostics();
    } catch (e) {
      toast.error("Sync failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const viewRun = async (id) => {
    try {
      const { data } = await api.get(`/admin/ingest-runs/${id}`);
      setSelected(data);
    } catch (e) {
      toast.error("Failed to load run");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-ingest-panel">
      <div
        className="rounded border border-white/10 bg-white/[0.02] px-3 py-2 text-[11px] text-white/55"
        data-testid="admin-readiness-state"
        data-core-state={
          selectedControlsReady
            ? coreState === "stale" ? "stale" : "ready"
            : operatorCoreFailed ? "failed" : "loading"
        }
        data-secondary-state={secondaryState}
        data-selected-operator={selectedOperator?.key || ""}
        data-selected-run-phase={selectedRunRefresh.current.key === selectedOperator?.key ? selectedRunRefresh.current.phase : ""}
        data-selected-run-revision={selectedRunRefresh.current.key === selectedOperator?.key ? selectedRunRefresh.current.revision : 0}
        data-selected-audit-run-revision={selectedAudit.key === selectedOperator?.key ? selectedAudit.runRefreshRevision ?? 0 : 0}
        data-selected-audit-status={selectedAudit.key === selectedOperator?.key ? selectedAudit.httpStatus ?? "" : ""}
        data-selected-audit-operator={selectedAudit.key === selectedOperator?.key ? selectedAudit.settings?.operator_key || "" : ""}
        data-selected-audit-expire-enabled={selectedAudit.key === selectedOperator?.key ? String(selectedAudit.settings?.expire_enabled) : ""}
        data-selected-audit-scheduler-enabled={selectedAudit.key === selectedOperator?.key ? String(selectedAudit.settings?.scheduler_enabled) : ""}
        data-outstanding-requests={outstandingRequests.join(",")}
        data-last-api-error={
          typeof readinessError === "string"
            ? readinessError
            : JSON.stringify(readinessError || "")
        }
        data-settings-loaded={selectedSettingsLoaded ? "true" : "false"}
        data-counts-loaded={diagnosticsState === "ready" ? "true" : "false"}
        data-core-elapsed-ms={coreReadyMs ?? ""}
        data-runs-elapsed-ms={runsElapsedMs ?? ""}
        data-diagnostics-elapsed-ms={diagnosticsElapsedMs ?? ""}
      >
        <span
          data-testid={
            selectedControlsReady
              ? "admin-core-ready"
              : operatorCoreFailed
              ? "admin-load-failed"
              : "admin-core-loading"
          }
        >
          {selectedControlsReady
            ? coreState === "stale"
              ? "Operator controls available with stale settings; refresh failed."
              : `Operator controls ready${coreReadyMs == null ? "" : ` in ${coreReadyMs}ms`}.`
            : operatorCoreFailed
            ? "Operator controls unavailable."
            : "Loading operator controls..."}
        </span>
        <span className="mx-2 text-white/25">|</span>
        <span
          data-testid={
            secondaryState === "ready"
              ? "admin-secondary-data-ready"
              : secondaryState === "error"
              ? "admin-secondary-data-unavailable"
              : "admin-secondary-data-loading"
          }
        >
          {secondaryState === "ready"
            ? "Secondary data ready."
            : secondaryState === "error"
            ? "Some secondary data is unavailable."
            : "Secondary data loading."}
        </span>
      </div>

      {coreState === "loading" && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5 text-sm text-white/50">
          Loading operator inventory and settings...
        </div>
      )}
      {operatorCoreFailed && (
        <div className="rounded-lg border border-red-400/25 bg-red-400/10 p-5 text-sm text-red-100">
          {typeof readinessError === "string" ? readinessError : "Operator controls unavailable."}
        </div>
      )}

      {operatorInventoryReady && (
        <OperatorSelector
          operators={adminOperatorOptions}
          selectedKey={selectedOperator?.optionKey || ""}
          onChange={setSelectedOperatorOptionKey}
        />
      )}

      {operatorInventoryReady && selectedSettingsRequired && !selectedSettingsLoaded && !operatorCoreFailed && (
        <div className="text-sm text-white/50" role="status">Loading selected operator audit...</div>
      )}
      {operatorInventoryReady && selectedSettingsRequired && selectedSettingsLoaded && selectedAudit.loading && (
        <div className="text-sm text-white/50" role="status">Refreshing selected operator audit...</div>
      )}

      {operatorCoreReady && selectedOperator && (
        <div>
          <div className="overline text-[var(--cc-gold)] mb-2">
            Selected operator controls
          </div>
          <SelectedOperatorSummary operator={selectedOperator} />
          <div className="mt-3 space-y-3">
            <OperatorAutomationSettingsCard
              operator={selectedOperator}
              settings={selectedActionSettings}
              diag={diag}
              onUpdated={handleAutomationSettingsUpdated}
            />
            {selectedOperator.supports.operatorAutomation && (
              <OperatorExecutionLeaseCard
                operator={selectedOperator}
                onStatusChange={setSelectedExecutionLeaseStatus}
                refreshToken={executionLeaseRefreshToken}
              />
            )}
            {selectedOperator.supports.trustedAutomation && (
              <TrustedAutomationCard
                diag={diag}
                operatorKeys={[selectedOperator.key]}
                compact
                onRun={() => {
                  refresh();
                  refreshDiagnostics();
                }}
              />
            )}
            {selectedOperator.supports.dcgV2 && (
              <DcgPipelineV2Card
                diag={diag}
                onApplied={() => {
                  refresh();
                  refreshDiagnostics();
                }}
              />
            )}
            {selectedOperator.supports.operatorAutomation && (
              <OperatorAutomationRunCard
                operator={selectedOperator}
                settings={selectedActionSettings}
                diag={diag}
                executionLeaseStatus={selectedExecutionLeaseStatus}
                onRun={(data) => {
                  selectedRunRefresh.current = {
                    key: selectedOperator.key,
                    phase: data?.phase || "",
                    revision: selectedRunRefresh.current.revision + 1,
                  };
                  setSelectedExecutionLeaseStatus(null);
                  setExecutionLeaseRefreshToken((current) => current + 1);
                  refresh();
                  refreshDiagnostics();
                }}
              />
            )}
            {selectedOperator.supports.liveListingDryRun && (
              <LiveListingIngestCard
                diag={diag}
                selectedOperator={selectedOperator}
                onApplied={() => {
                  refresh();
                  refreshDiagnostics();
                }}
              />
            )}
            {!selectedOperator.supports.trustedAutomation &&
              !selectedOperator.supports.operatorAutomation &&
              !selectedOperator.supports.dcgV2 &&
              !selectedOperator.supports.liveListingDryRun && (
                <OperatorNoActionNotice operator={selectedOperator} />
              )}
          </div>
        </div>
      )}

      {/* Diagnostics strip */}
      {diagnosticsState === "loading" && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-xs text-white/45">
          Loading diagnostics...
        </div>
      )}
      {diagnosticsState === "error" && (
        <div className="rounded-lg border border-amber-400/25 bg-amber-400/10 p-4 text-xs text-amber-100">
          Diagnostics unavailable. Operator controls remain available.
        </div>
      )}
      {diagnosticsState === "ready" && diag && (
        <div
          className="rounded-lg border border-white/10 bg-white/[0.02] p-4"
          data-testid="diagnostics-strip"
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
            <div className="text-xs text-white/60 flex flex-wrap items-center gap-3">
              <span className={`px-2 py-0.5 rounded font-mono-num text-[10px] uppercase ${diag.is_production ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                {diag.environment}
              </span>
              <span>DB: <code className="text-white/80">{diag.db_name}</code></span>
              <span>Operators: <strong className="text-white">{diag.operator_count}</strong></span>
              <span>Ingest-enabled: <strong className="text-white">{diag.ingest_enabled_operator_count}</strong></span>
              <span>Competitions: <strong className="text-white">{diag.competition_count}</strong></span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <label
                className="inline-flex items-center gap-1.5 text-[11px] text-white/70 cursor-pointer"
                title="Bypass the 24h per-query cache. Useful when debugging or after a key rotation."
              >
                <input
                  type="checkbox"
                  checked={forceBraveRefresh}
                  onChange={(e) => setForceBraveRefresh(e.target.checked)}
                  data-testid="brave-force-refresh-checkbox"
                />
                Force Brave refresh
              </label>
              <button
                onClick={syncOperatorDirectory}
                disabled={syncing}
                data-testid="sync-operator-directory-btn"
                className="btn-gold px-3 py-1.5 rounded text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <CloudArrowDown size={14} weight="bold" /> {syncing ? "Syncing…" : "Sync operator directory"}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3 text-[11px]">
            <Pill label="Verified live" value={diag.verified_live_count} testId="diag-verified-live" tone="ok" />
            <Pill label="Pending review" value={diag.source_pending_review_count} testId="diag-pending" tone="warn" />
            <Pill label="Source unverified" value={diag.source_unverified_count} testId="diag-unverified" tone="warn" />
            <Pill label="Broken" value={diag.broken_link_count} testId="diag-broken" tone="bad" />
            <Pill label="Expired" value={diag.expired_count} testId="diag-expired" tone="bad" />
            <Pill label="Hidden" value={diag.hidden_count} testId="diag-hidden" tone="muted" />
          </div>
        </div>
      )}

      <div>
        <div className="overline text-amber-300 mb-2">
          Production Maintenance · V2 Reset / Backup
        </div>
        <V2ResetMaintenanceCard />
      </div>

      <div>
        <div className="overline text-[var(--cc-gold)] mb-2">
          Odds &amp; Image Enrichment
        </div>
        <OddsEnrichmentCard
          diag={diag}
          onApplied={() => {
            refresh();
            refreshDiagnostics();
          }}
        />
      </div>

      <div>
        <div className="overline text-[var(--cc-gold)] mb-2">
          Repair Live Data
        </div>
        <LiveRepairCard
          diag={diag}
          onApplied={() => {
            refresh();
            refreshDiagnostics();
          }}
        />
      </div>

      <div>
        <div className="overline text-white/50 mb-2">
          Search-index discovery (secondary / research mode)
        </div>
        <p className="text-xs text-white/40 mb-3 leading-relaxed max-w-2xl">
          Brave Search + direct sitemap discovery. Demoted to a secondary
          channel since Iter 20.18 — operator live/current listing pages
          are now the primary source. Use these for research or to
          surface candidates that don't appear on the operator's own
          listing page.
        </p>
        {selectedOperator?.supports.searchIndex && selectedBackendOperator?.ingest_enabled ? (
          <div
            className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3"
            data-testid="selected-operator-search-index-actions"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-white">{selectedBackendOperator.name}</div>
                <div className="text-[11px] text-white/50">
                  {selectedBackendOperator.canonical_domain || selectedBackendOperator.website_url}
                </div>
              </div>
              <span className="px-2 py-0.5 rounded text-[10px] uppercase font-semibold bg-white/10 text-white/70">
                {selectedBackendOperator.ingest_strategy || "manual"}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] text-white/50">
              <span>
                Last:{" "}
                {selectedBackendOperator.last_ingested_at
                  ? new Date(selectedBackendOperator.last_ingested_at).toLocaleString("en-GB")
                  : "never"}
              </span>
              {selectedBackendOperator.last_ingest_status && (
                <span
                  className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${
                    STATUS_STYLES[selectedBackendOperator.last_ingest_status] || "bg-white/10 text-white/70"
                  }`}
                >
                  {selectedBackendOperator.last_ingest_status}
                </span>
              )}
              {selectedBackendOperator.blocked_by_cloudflare && (
                <span className="text-red-300 inline-flex items-center gap-1">
                  <Warning size={12} /> Cloudflare-blocked
                </span>
              )}
              {selectedBackendOperator.requires_js && (
                <span className="text-amber-300 inline-flex items-center gap-1">
                  <Warning size={12} /> Requires JS rendering
                </span>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <button
                onClick={() => triggerIngest(selectedBackendOperator)}
                disabled={busyOp === selectedBackendOperator.id}
                data-testid={`ingest-run-btn-${selectedBackendOperator.id}`}
                className="btn-gold py-2 rounded text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <ArrowsClockwise size={12} weight="bold" /> {busyOp === selectedBackendOperator.id ? "Running…" : "Run direct ingest"}
              </button>
              {(selectedBackendOperator.can_run_brave_discovery || selectedBackendOperator.brave_search_enabled) && (
                <button
                  onClick={() => triggerBraveDiscovery(selectedBackendOperator)}
                  disabled={busyOp === selectedBackendOperator.id || !diag?.brave_search_configured}
                  data-testid={`brave-discovery-btn-${selectedBackendOperator.id}`}
                  title={!diag?.brave_search_configured ? "Configure Brave key in Providers tab" : ""}
                  className="py-2 rounded text-xs inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/15 text-white disabled:opacity-50"
                >
                  <CloudArrowDown size={12} weight="bold" /> Run Brave discovery
                </button>
              )}
              <button
                onClick={() => triggerPrefillAll(selectedBackendOperator)}
                disabled={busyOp === selectedBackendOperator.id}
                data-testid={`prefill-all-btn-${selectedBackendOperator.id}`}
                className="py-2 rounded text-xs inline-flex items-center justify-center gap-1.5 bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 disabled:opacity-50"
              >
                Prefill pending candidates
              </button>
              <button
                onClick={() => triggerOperatorEvidence(selectedBackendOperator)}
                disabled={busyOp === selectedBackendOperator.id}
                data-testid={`operator-prefill-btn-${selectedBackendOperator.id}`}
                className="py-2 rounded text-xs inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 text-white/80 disabled:opacity-50"
              >
                Prefill operator metadata
              </button>
            </div>
          </div>
        ) : (
          <div className="border border-dashed border-white/15 rounded-lg p-6 text-center text-white/40 text-sm" data-testid="ingest-empty-state">
            {selectedOperator
              ? `${selectedOperator.name} is not enabled for search-index discovery in this selected-operator view.`
              : "No operator selected."}
          </div>
        )}
      </div>

      <div>
        <div className="overline text-[var(--cc-gold)] mb-2">Recent ingest runs</div>
        {runsState === "loading" ? (
          <div className="text-xs text-white/40">Loading ingest history...</div>
        ) : runsState === "error" ? (
          <div className="text-xs text-amber-200">Ingest history unavailable.</div>
        ) : runs.length === 0 ? (
          <div className="text-xs text-white/40">No ingest runs yet.</div>
        ) : (
          <div className="rounded-lg border border-white/10 overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-white/5 text-white/60">
                <tr>
                  <th className="text-left px-4 py-3">Operator</th>
                  <th className="text-left px-4 py-3">Started</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Discovered</th>
                  <th className="text-right px-4 py-3">Ingested</th>
                  <th className="text-right px-4 py-3">Updated</th>
                  <th className="text-right px-4 py-3">Rejected</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t border-white/5" data-testid={`ingest-run-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold">{r.operator_name}</td>
                    <td className="px-4 py-3 text-white/70 text-xs">{new Date(r.started_at).toLocaleString("en-GB")}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${STATUS_STYLES[r.status] || "bg-white/10 text-white/70"}`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono-num">{r.discovered_count || 0}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-emerald-300">{r.ingested_count || 0}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-amber-300">{r.updated_count || 0}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-red-300">{r.rejected_count || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => viewRun(r.id)} className="text-white/60 hover:text-white text-xs underline">
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          data-testid="ingest-run-detail-modal"
        >
          <div className="bg-[var(--cc-card-dark)] rounded-lg border border-white/10 w-full max-w-3xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <h3 className="font-display text-xl">Ingest run · {selected.operator_name}</h3>
                <div className="text-xs text-white/50">{selected.id}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-white/60 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <div className="flex gap-4 flex-wrap">
                <Stat label="Status" value={selected.status} />
                <Stat label="Discovered" value={selected.discovered_count || 0} />
                <Stat label="Validated" value={selected.validated_count || 0} />
                <Stat label="Ingested" value={selected.ingested_count || 0} />
                <Stat label="Updated" value={selected.updated_count || 0} />
                <Stat label="Rejected" value={selected.rejected_count || 0} />
              </div>
              {selected.warnings?.length > 0 && (
                <div className="rounded bg-amber-500/10 border border-amber-500/30 p-3 text-amber-200 text-xs">
                  <div className="font-semibold mb-1">Warnings</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {selected.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              <div>
                <div className="overline text-white/50 mb-2">Per-URL results</div>
                <div className="max-h-80 overflow-y-auto rounded border border-white/10">
                  <table className="w-full text-xs">
                    <thead className="bg-white/5 text-white/60 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">URL</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-left px-3 py-2">Validation</th>
                        <th className="text-right px-3 py-2">Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selected.results || []).map((row, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="px-3 py-2 max-w-[300px] truncate">{row.url}</td>
                          <td className="px-3 py-2">{row.status}</td>
                          <td className="px-3 py-2">{row.validation || "—"}</td>
                          <td className="px-3 py-2 text-right font-mono-num">{row.confidence ?? "—"}</td>
                        </tr>
                      ))}
                      {(selected.results || []).length === 0 && (
                        <tr><td colSpan={4} className="px-3 py-4 text-center text-white/40">No per-URL results.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded bg-white/[0.04] border border-white/10 px-3 py-2 min-w-[90px]">
    <div className="overline text-white/40">{label}</div>
    <div className="font-mono-num text-sm font-semibold">{value}</div>
  </div>
);

const MODE_STYLES = {
  ok: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  warn: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  info: "bg-sky-400/15 text-sky-100 border-sky-400/30",
  muted: "bg-white/5 text-white/60 border-white/10",
};

const OperatorSelector = ({ operators, selectedKey, onChange }) => (
  <div
    className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3"
    data-testid="auto-ingest-operator-selector"
  >
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div>
        <div className="overline text-[var(--cc-gold)] mb-1">
          Auto-ingest operator
        </div>
        <p className="text-xs text-white/55 max-w-2xl leading-relaxed">
          Pick one operator to inspect and run supported actions. This keeps
          trusted automation, live-listing dry-runs and search-index tools in a
          single selected-operator workspace.
        </p>
      </div>
      <select
        value={selectedKey}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-64 rounded bg-black/30 border border-white/15 px-3 py-2 text-sm text-white"
        data-testid="auto-ingest-operator-dropdown"
      >
        {operators.map((operator) => (
          <option key={operator.optionKey} value={operator.optionKey}>
            {operator.name}
          </option>
        ))}
      </select>
    </div>
  </div>
);

export const SelectedOperatorSummary = ({ operator }) => (
  <div
    className="rounded-lg border border-white/10 bg-white/[0.02] p-4"
    data-testid="selected-operator-summary"
  >
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-lg font-semibold text-white">{operator.name}</h3>
          <span
            className={`px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider font-semibold ${
              MODE_STYLES[operator.tone] || MODE_STYLES.muted
            }`}
          >
            {operator.modeLabel}
          </span>
        </div>
        <div className="text-[11px] text-white/50 mt-1">
          {operator.domain || "No domain configured"}
        </div>
        <p className="text-xs text-white/60 mt-2 max-w-2xl leading-relaxed">
          {operator.summary}
        </p>
      </div>
      <div className="text-right text-[11px] text-white/45">
        <div>{operator.lastRunLabel || "Last run"}</div>
        <div className="text-white/75">
          {operator.lastRunAt
            ? new Date(operator.lastRunAt).toLocaleString("en-GB")
            : "never"}
        </div>
        {operator.lastRunStatus && (
          <div className="mt-1 uppercase">{operator.lastRunStatus}</div>
        )}
      </div>
    </div>
    <div className="mt-3 flex flex-wrap gap-1.5">
      {operator.supportedActions.map((action) => (
        <span
          key={action}
          className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-[10px] text-white/60"
        >
          {action}
        </span>
      ))}
    </div>
  </div>
);

const OperatorNoActionNotice = ({ operator }) => (
  <div
    className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-sm text-white/60"
    data-testid="selected-operator-no-action"
  >
    <div className="font-semibold text-white mb-1">{operator.modeLabel}</div>
    <p className="text-xs leading-relaxed">{operator.summary}</p>
  </div>
);

const Pill = ({ label, value, testId, tone }) => {
  const toneClass = {
    ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    warn: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    bad: "bg-red-500/15 text-red-300 border-red-500/30",
    muted: "bg-white/5 text-white/60 border-white/10",
  }[tone] || "bg-white/5 text-white/60 border-white/10";
  return (
    <div className={`rounded px-2 py-1.5 border text-[10px] ${toneClass}`} data-testid={testId}>
      <div className="font-mono-num text-sm font-semibold leading-tight">{value ?? 0}</div>
      <div className="opacity-80">{label}</div>
    </div>
  );
};
